import React, { useEffect, useState } from "react";
import {
  Menu,
  Search,
  BriefcaseBusiness,
  UserRoundPlus,
  ArrowRight,
  ShieldCheck,
  MapPin
} from "lucide-react";

import ProfessionCategories from "./ProfessionCategories";
import ProfessionalSuggestion from "./ProfessionalSuggestion";

import ProfessionSetupModal from "./ProfessionSetupModal";


import "./profession.dashboard.css";

import { useNavigate } from "react-router-dom";
import BottomNavigation from "../../common/BottomNavigation";

export default function ProfessionDashboard() {
  const [user, setUser] = useState(null);
  const [search, setSearch] = useState("");

  const [showMenu, setShowMenu] = useState(false);
  const [showSetup, setShowSetup] = useState(false);

  const [selectedProfession, setSelectedProfession] =
    useState("");

  const [availableProfessions, setAvailableProfessions] =
    useState([]);

  const navigate = useNavigate();

  /* =====================================================
     LOAD USER
  ===================================================== */

  useEffect(() => {
    const raw = localStorage.getItem("civilink_user");

    if (raw) {
      try {
        setUser(JSON.parse(raw));
      } catch (err) {
        console.error("Invalid Civilink user:", err);
      }
    }
  }, []);

  /* =====================================================
     LOAD PROFESSIONS
  ===================================================== */

  useEffect(() => {
    const loadProfessions = async () => {
      try {
        const res = await fetch(
          "/api/profession/available"
        );

        const data = await res.json();

        if (data.success) {
          setAvailableProfessions(
            data.professions || []
          );
        }
      } catch (err) {
        console.error(
          "Failed to load professions:",
          err
        );
      }
    };

    loadProfessions();
  }, []);

  /* =====================================================
     PROFESSION SETUP
  ===================================================== */

  const handleSelectProfession = (profession) => {
    if (!user) {
      navigate("/");
      return;
    }

    setSelectedProfession(profession);
    setShowSetup(true);
    setShowMenu(false);
  };

  /* =====================================================
     VIEW PROFESSION
  ===================================================== */

  const handleViewProfession = (profession) => {
    navigate(
      `/profession/${profession.toLowerCase()}`
    );
  };

  /* =====================================================
     USER DISPLAY
  ===================================================== */

  const firstName =
    user?.name?.split(" ")?.[0] || "there";

  const currentProfession =
    user?.profession &&
    user.profession !== "Member"
      ? user.profession
      : null;

  const userCity =
    user?.location?.city || "";

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div className="pro-dashboard-container">

      <main className="pro-dashboard-content">

        {/* =================================================
            HEADER
        ================================================= */}

        <header className="pro-header">

          <button
            type="button"
            className="pro-menu-btn"
            onClick={() => setShowMenu(true)}
            aria-label="Open profession menu"
          >
            <Menu
              size={21}
              strokeWidth={2}
            />
          </button>

          <div className="pro-header-brand">

            <span className="pro-header-label">
              CIVILINK
            </span>

            <h1>
              Profession Hub
            </h1>

          </div>

        </header>


        {/* =================================================
            WELCOME
        ================================================= */}

        <section className="pro-welcome">

          <div className="pro-welcome-copy">

            <span className="pro-eyebrow">
              PROFESSIONAL NETWORK
            </span>

            <h2>
              Welcome, {firstName}.
            </h2>

            <p>
              Build your professional identity,
              connect with the construction community
              and discover opportunities.
            </p>

          </div>

          {currentProfession && (
            <div className="pro-current-role">

              <div className="pro-current-role-icon">
                <ShieldCheck
                  size={18}
                />
              </div>

              <div>
                <span>
                  YOUR PROFESSION
                </span>

                <strong>
                  {currentProfession}
                </strong>
              </div>

            </div>
          )}

        </section>


        {/* =================================================
            SEARCH
        ================================================= */}

        <section className="pro-search-section">

          <div className="pro-search-label">
            Find people and expertise
          </div>

          <div className="pro-search-box">

            <Search
              size={19}
              className="pro-search-icon"
            />

            <input
              type="text"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
              placeholder="People, skills or locations..."
              className="pro-search-input"
              aria-label="Search professionals"
            />

          </div>

          {userCity && (
            <div className="pro-search-location">

              <MapPin size={13} />

              <span>
                Showing professionals around{" "}
                {userCity}
              </span>

            </div>
          )}

        </section>


        {/* =================================================
            PROFESSIONAL STATUS
        ================================================= */}

        {!currentProfession ? (
          <section className="pro-status-card">

            <div className="pro-status-icon">
              <UserRoundPlus size={22} />
            </div>

            <div className="pro-status-content">

              <span className="pro-card-label">
                PROFESSIONAL IDENTITY
              </span>

              <h2>
                Build your professional profile
              </h2>

              <p>
                Choose your profession and create
                your Civilink professional identity.
              </p>

              <button
                type="button"
                className="pro-primary-action"
                onClick={() =>
                  setShowMenu(true)
                }
              >
                Choose profession
                <ArrowRight size={16} />
              </button>

            </div>

          </section>
        ) : (
          <section className="pro-status-card active">

            <div className="pro-status-icon">
              <ShieldCheck size={22} />
            </div>

            <div className="pro-status-content">

              <span className="pro-card-label">
                PROFESSIONAL IDENTITY
              </span>

              <h2>
                Your {currentProfession} profile
              </h2>

              <p>
                Your professional identity is
                available on Civilink.
              </p>

              <button
                type="button"
                className="pro-primary-action"
                onClick={() =>
                  navigate("/profile")
                }
              >
                View profile
                <ArrowRight size={16} />
              </button>

            </div>

          </section>
        )}


        {/* =================================================
            QUICK ACTIONS
        ================================================= */}

        <section className="pro-section">

          <div className="pro-section-heading">

            <div>
              <span className="pro-section-eyebrow">
                QUICK ACCESS
              </span>

              <h2>
                Get things done
              </h2>
            </div>

          </div>

          <div className="pro-quick-actions">

            <button
  type="button"
  className="pro-action-card"
  onClick={() => {
    sessionStorage.setItem(
      "civilink_open_post_work",
      "true"
    );

    navigate("/profile");
  }}
>
  <div className="pro-action-icon blue">
    <BriefcaseBusiness size={21} />
  </div>

  <div className="pro-action-content">
    <strong>
      Post a work
    </strong>

    <span>
      Find professionals for your project
    </span>
  </div>

  <ArrowRight
    size={17}
    className="pro-action-arrow"
  />
</button>


            <button
              type="button"
              className="pro-action-card"
              onClick={() => navigate("/find-work")}
            >

              <div className="pro-action-icon green">
                <BriefcaseBusiness size={21} />
              </div>

              <div className="pro-action-content">

                <strong>
                  Find work
                </strong>

                <span>
                  Construction opportunities
                </span>

              </div>

              <ArrowRight
                size={17}
                className="pro-action-arrow"
              />

            </button>

          </div>

        </section>


        {/* =================================================
            EXPLORE PROFESSIONS
        ================================================= */}

        <section className="pro-section">

          <div className="pro-section-heading">

            <div>

              <span className="pro-section-eyebrow">
                DISCOVER
              </span>

              <h2>
                Explore professions
              </h2>

              <p>
                Find the right construction
                professional for your needs.
              </p>

            </div>

          </div>

          <ProfessionCategories
  data={availableProfessions}
  onSelect={handleViewProfession}
/>



        </section>


        {/* =================================================
            RECOMMENDED PROFESSIONALS
        ================================================= */}

        <section className="pro-section">

          <div className="pro-section-heading">

            <div>

              <span className="pro-section-eyebrow">
                CIVILINK NETWORK
              </span>

              <h2>
                Recommended professionals
              </h2>

              <p>
                Discover professionals from
                the construction community.
              </p>

            </div>

          </div>

          <ProfessionalSuggestion
            search={search}
          />

        </section>


        <div className="pro-bottom-space" />

      </main>


      {/* =================================================
          PROFESSION MENU
      ================================================= */}

      {showMenu && (
        <div
          className="pro-menu-backdrop"
          onClick={() =>
            setShowMenu(false)
          }
        >

          <aside
            className="pro-slide-menu"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            <div className="pro-menu-header">

              <div>

                <span className="pro-section-eyebrow">
                  CIVILINK
                </span>

                <h3 className="menu-title">
                  Choose your profession
                </h3>

                <p className="menu-subtitle">
                  Build your professional identity.
                </p>

              </div>

              <button
                type="button"
                className="pro-menu-header-close"
                onClick={() =>
                  setShowMenu(false)
                }
                aria-label="Close"
              >
                ×
              </button>

            </div>


            {[
              "Worker",
              "Engineer",
              "Architect",
              "Contractor",
              "Real Estate Dealer"
            ].map((profession) => (

              <button
                key={profession}
                type="button"
                className="menu-item"
                onClick={() =>
                  handleSelectProfession(
                    profession
                  )
                }
              >

                <span>
                  Become {profession}
                </span>

                <ArrowRight size={17} />

              </button>

            ))}


            <button
              type="button"
              className="menu-close"
              onClick={() =>
                setShowMenu(false)
              }
            >
              Close
            </button>

          </aside>

        </div>
      )}


      {/* =================================================
          PROFESSION SETUP
      ================================================= */}

      <ProfessionSetupModal
        open={showSetup}
        onClose={() =>
          setShowSetup(false)
        }
        profession={selectedProfession}
        user={user}
        onComplete={(profession) => {

          if (
            ["Engineer", "Architect"].includes(
              profession
            )
          ) {
            navigate(
              "/profile?verification=pending"
            );
          } else {
            navigate("/profile");
          }

        }}
      />


      {/* =================================================
          BOTTOM NAVIGATION
      ================================================= */}

      <BottomNavigation
        onPost={() => {

          sessionStorage.setItem(
            "civilink_open_post",
            "true"
          );

          navigate("/home");

        }}

        onCustomerMenu={() => {

          sessionStorage.setItem(
            "civilink_open_customer",
            "true"
          );

          navigate("/home");

        }}
      />

    </div>
  );
}